import { supabase } from './authService';
import { StoreGame, GameScore, TotoRound, TotoMatch, TotoPrediction, Language } from '../types';

export const gameService = {
  getStoreGames: async (): Promise<StoreGame[]> => {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .order('title', { ascending: true });

    if (error) {
      console.error('Error fetching store games:', error);
      return [];
    }

    return (data || []).map(g => ({
      ...g,
      is_hidden: !!g.is_hidden
    })) as StoreGame[];
  },

  saveScore: async (gameId: string, score: number): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const now = new Date();
      const year = now.getFullYear();
      
      // Calculate week number
      const firstDayOfYear = new Date(year, 0, 1);
      const pastDaysOfYear = (now.getTime() - firstDayOfYear.getTime()) / 86400000;
      const week = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);

      const { error } = await supabase
        .from('scores')
        .insert([{
          user_id: user.id,
          game_id: gameId,
          score: score,
          year: year,
          week: week
        }]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error saving score:', error);
      return false;
    }
  },

    getTopScores: async (gameId: string, limit: number = 10): Promise<any[]> => {
    // scores 테이블과 user 테이블 사이의 명시적인 join 관계가 없을 경우를 대비해 
    // 각각 조회하거나 쿼리 구조를 단순화합니다.
    // 더 많은 데이터를 가져와서 유저별 중복을 제거한 후 limit을 적용합니다.
    const { data, error } = await supabase
      .from('scores')
      .select(`
        score,
        user_id
      `)
      .eq('game_id', gameId)
      .order('score', { ascending: false })
      .limit(limit * 5); // 중복 제거를 고려하여 넉넉하게 가져옴

    if (error) {
      console.error('Error fetching top scores:', error);
      return [];
    }

    if (!data || data.length === 0) return [];

    // 유저 정보 mapping
    const userIds = [...new Set(data.map(d => d.user_id))];
    const { data: userData, error: userError } = await supabase
      .from('user')
      .select('id, name')
      .in('id', userIds);

    const userMap: Record<string, string> = {};
    if (!userError && userData) {
      userData.forEach(u => {
        userMap[u.id] = u.name;
      });
    }

    // 유저별로 그룹화하여 최고 점수만 추출하거나, 
    // 동일 유저인 경우 점수 높은 순으로 이미 정렬되어 있지만 
    // 명시적으로 랭킹을 계산할 때 로직을 확인합니다.
    const uniqueRankings: any[] = [];
    const userBestScore: Record<string, boolean> = {};

    data.forEach((item) => {
      if (!userBestScore[item.user_id]) {
        userBestScore[item.user_id] = true;
        uniqueRankings.push({
          nickname: userMap[item.user_id] || 'Unknown',
          score: item.score,
          user_id: item.user_id
        });
      }
    });

    return uniqueRankings.slice(0, limit).map((item, index) => ({
      rank: index + 1,
      nickname: item.nickname,
      score: item.score
    }));
  },

  createStoreGame: async (game: Omit<StoreGame, 'id'>): Promise<StoreGame | null> => {
    const { data, error } = await supabase
      .from('games')
      .insert([game])
      .select()
      .single();

    if (error) {
      console.error('Error creating store game:', error);
      throw error;
    }

    return data as StoreGame;
  },

  updateStoreGame: async (id: string, game: Partial<StoreGame>): Promise<StoreGame | null> => {
    const { data, error } = await supabase
      .from('games')
      .update(game)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating store game:', error);
      throw error;
    }

    return data as StoreGame;
  },

  deleteStoreGame: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('games')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting store game:', error);
      throw error;
    }

    return true;
  },

  deleteTotoRound: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('toto_rounds')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting toto round:', error);
      return false;
    }

    return true;
  },

  // Toto Rounds
  getTotoRounds: async (): Promise<TotoRound[]> => {
    const { data, error } = await supabase
      .from('toto_rounds')
      .select('*')
      .order('round_number', { ascending: false });
    return error ? [] : data as TotoRound[];
  },

  createTotoRound: async (round: Omit<TotoRound, 'id'>): Promise<TotoRound | null> => {
    const { data, error } = await supabase
      .from('toto_rounds')
      .insert([round])
      .select()
      .single();
    return error ? null : data as TotoRound;
  },

  updateTotoRound: async (id: string, round: Partial<TotoRound>): Promise<TotoRound | null> => {
    const { data, error } = await supabase
      .from('toto_rounds')
      .update(round)
      .eq('id', id)
      .select()
      .single();
    return error ? null : data as TotoRound;
  },

  // Toto Matches
  getTotoMatches: async (roundId: string): Promise<TotoMatch[]> => {
    const { data, error } = await supabase
      .from('toto_matches')
      .select('*')
      .eq('round_id', roundId)
      .order('order_index', { ascending: true });
    return error ? [] : data as TotoMatch[];
  },

  addTotoMatches: async (matches: Omit<TotoMatch, 'id'>[]): Promise<boolean> => {
    const { error } = await supabase
      .from('toto_matches')
      .insert(matches);
    return !error;
  },

  // Toto Predictions
  getUserTotoPrediction: async (roundId: string, userId: string): Promise<TotoPrediction | null> => {
    const { data, error } = await supabase
      .from('toto_predictions')
      .select('*')
      .eq('round_id', roundId)
      .eq('user_id', userId)
      .maybeSingle();
    return error ? null : data as TotoPrediction;
  },

  getTotoMatchAnalysis: async (matchId: string, userId: string): Promise<any | null> => {
    const { data, error } = await supabase
      .from('match_predictions')
      .select('*')
      .eq('post_id', matchId)
      .eq('user_id', userId)
      .maybeSingle();
    return error ? null : data;
  },

  saveTotoMatchAnalysis: async (matchId: string, userId: string, analysis: any, match?: any): Promise<boolean> => {
    // If match object is provided, we can populate home_team_id and away_team_id
    const homeTeamId = match?.home_team_id || null;
    const awayTeamId = match?.away_team_id || null;

    const { error } = await supabase
      .from('match_predictions')
      .insert({
        post_id: matchId,
        user_id: userId,
        predicted_winner: analysis.predicted_winner,
        predicted_score: analysis.predicted_score,
        win_probability: analysis.win_probability,
        analysis_reason: analysis.analysis_reason || analysis.analysis_reason_en || '',
        analysis_reason_ko: analysis.analysis_reason_ko || '',
        // analysis_reason_ja: analysis.analysis_reason_ja || null,
        // analysis_reason_es: analysis.analysis_reason_es || null,
        // analysis_reason_fr: analysis.analysis_reason_fr || null,
        home_team_id: homeTeamId,
        away_team_id: awayTeamId,
        league_id: analysis.league_id || null,
        matchdate: analysis.matchdate || null,
        updated_at: new Date().toISOString()
      });
      // The prompt didn't mention unique constraint on (post_id, user_id) for match_predictions,
      // so we just use insert. If there is a unique constraint, we'd need upsert.
    return !error;
  },

  saveTotoPrediction: async (prediction: Omit<TotoPrediction, 'id' | 'correct_count' | 'is_rewarded'>): Promise<boolean> => {
    const { data: existing } = await supabase
      .from('toto_predictions')
      .select('id')
      .eq('round_id', prediction.round_id)
      .eq('user_id', prediction.user_id)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase
        .from('toto_predictions')
        .update({ predictions: prediction.predictions })
        .eq('id', existing.id);
      return !error;
    } else {
      const { error } = await supabase
        .from('toto_predictions')
        .insert([{ ...prediction, correct_count: 0, is_rewarded: false }]);
      return !error;
    }
  },

  getTotoRoundParticipation: async (roundId: string): Promise<{ count: number; users: { avatar: string | null }[] }> => {
    const { data, error, count } = await supabase
      .from('toto_predictions')
      .select('user_id', { count: 'exact' })
      .eq('round_id', roundId);

    if (error || !data) return { count: 0, users: [] };

    // 참여 유저들의 아바타 정보를 가져옵니다.
    const userIds = data.map(d => d.user_id);
    const { data: userData } = await supabase
      .from('user')
      .select('avatar_url')
      .in('id', userIds)
      .limit(3);

    return {
      count: count || 0,
      users: (userData || []).map(u => ({ avatar: u.avatar_url }))
    };
  },

    updateTotoMatchResult: async (matchId: string, result: '1' | 'X' | '2'): Promise<boolean> => {
      const { error } = await supabase
        .from('toto_matches')
        .update({ result })
        .eq('id', matchId);
      return !error;
    },

    updateTotoPredictionRewardStatus: async (predictionId: string, isRewarded: boolean): Promise<boolean> => {
      const { error } = await supabase
        .from('toto_predictions')
        .update({ is_rewarded: isRewarded })
        .eq('id', predictionId);
      return !error;
    }
  };
